import type { EpubPreferences } from '@readium/navigator';
import type {
  MetadataUserDataSettingsResponse,
  NormalizedUserSettings,
} from '../models/metadata-response';
import type { ViewportService } from '../services/viewport/viewport';

/**
 * Converts a percentage string (e.g., "150%") to a decimal (e.g., 1.5)
 */
function parsePercentageToDecimal(value: string): number {
  const numericValue = Number.parseFloat(value.replace('%', ''));
  return numericValue / 100;
}

/**
 * Converts a decimal value (e.g., 1.5) to a percentage string (e.g., "150%")
 */
function parseDecimalToPercentage(value: number): string {
  const percentage = Math.round(value * 100);
  return `${percentage}%`;
}

/**
 * Deserializes user settings from database format to Readium2 format
 * @param settings Settings in database format (with percentages as strings)
 * @param viewportService ViewportService instance for margin calculations
 * @returns Deserialized settings for Readium2 (with decimal values)
 */
export function deserializeUserSettings(
  settings: MetadataUserDataSettingsResponse | undefined,
  viewportService: ViewportService,
): NormalizedUserSettings {
  if (!settings) return {};
  const fontSize = settings['font-size']?.includes('%')
    ? parsePercentageToDecimal(settings['font-size'])
    : Number(settings['font-size']);

  const lineHeight = settings['line-height']?.includes('%')
    ? parsePercentageToDecimal(settings['line-height'])
    : Number(settings['line-height']);

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  const columnCount = settings.pagination === 'one' ? 1 : 2;
  const textAlign = settings['text-align'] ?? undefined;
  const pageGutter = viewportService.calculateMarginFromPercentage(
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    settings['page-margin-percentage'] ?? 0.5,
  );

  return {
    fontSize,
    lineHeight,
    fontFamily: settings['font-family'],
    backgroundColor: settings.background,
    textColor: settings.color,
    columnCount,
    textAlign,
    pageGutter,
  };
}

/**
 * Serializes user settings from Readium2 format to database format
 * @param settings Settings in R2 format (with decimal values)
 * @param viewportService ViewportService instance for margin calculations
 * @returns Settings in database format (with percentages as strings)
 */
export function serializeUserSettings(
  settings: EpubPreferences,
  viewportService: ViewportService,
): MetadataUserDataSettingsResponse {
  let marginPercentage = 0.5;
  if (settings.pageGutter) {
    marginPercentage = viewportService.calculatePercentageFromMargin(settings.pageGutter);
  }
  return {
    ...(settings.fontSize && { 'font-size': parseDecimalToPercentage(settings.fontSize) }),
    ...(settings.lineHeight && { 'line-height': parseDecimalToPercentage(settings.lineHeight) }),
    ...(settings.fontFamily && { 'font-family': settings.fontFamily }),
    ...(settings.backgroundColor && { background: settings.backgroundColor }),
    ...(settings.textColor && { color: settings.textColor }),
    ...(settings.textAlign && { 'text-align': settings.textAlign }),
    ...(settings.pageGutter && { 'page-margin-percentage': marginPercentage }),
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    pagination: settings.columnCount === 2 ? 'two' : 'one',
  };
}
